import { useEffect, useRef, useState, type FormEvent } from "react";
import type { ClassSummary } from "../api";
import { t } from "../i18n";
import { Popover } from "./Popover";

// Giving a thing — an app template, a file — to a class its owner teaches
// (RFC-052 §5, RFC-054): only a class they are the instructor of, and not one
// that has it already. Started from Give to a class on the line that heads
// the classes it is given to, in a popover beside that list; given, it
// closes, and the rows say which classes have it (byj-1wv.51.14).

/**
 * The classes I teach, read once as the page opens: null until then, and
 * when they could not be read — so "you teach none" is said only when it is
 * so, never while loading or after a failure. `classes` is read once too,
 * whatever it is on later renders: a function made anew each render
 * (`() => api.classes()`) would otherwise read them again on every one, and
 * each answer render the page again.
 */
export function useTeaching(classes: () => Promise<ClassSummary[]>): ClassSummary[] | null {
  const [teaching, setTeaching] = useState<ClassSummary[] | null>(null);
  const read = useRef(classes);
  useEffect(() => {
    let live = true;
    read.current().then(
      (cs) => live && setTeaching(cs.filter((c) => c.role === "instructor")),
      () => live && setTeaching(null),
    );
    return () => {
      live = false;
    };
  }, []);
  return teaching;
}

/**
 * Give to a class, for the classes in `givable` (taught, not given yet);
 * nothing when there is none. `give` resolves true once the class has it,
 * and the popover closes, the focus back on its button; false (it failed,
 * and the page says why) leaves it open with the class still picked, to try
 * again. Given the last class there was, the button goes with the popover,
 * and `focusAfterLast` puts the focus somewhere that stays — the classes'
 * heading — rather than leaving it on nothing.
 */
export function GiveToClass({ givable, note, give, focusAfterLast }: {
  givable: ClassSummary[];
  /** what giving it lets the class's members do */
  note: string;
  give: (cls: string) => Promise<boolean>;
  focusAfterLast?: () => void;
}) {
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  if (givable.length === 0) return null;
  // a class picked and since given (elsewhere, or by the last press) is no longer offered
  const value = givable.some((c) => c.id === pick) ? pick : givable[0]!.id;
  return (
    <Popover label={t("templates:templateGiveTitle")} button={t("templates:templateGiveTitle")} buttonClassName="small" className="pop" popClassName="pop-panel" focus="select">
      {(close) => (
        <form
          className="stack"
          data-testid="give-to-class"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            // the last there was: once given, nothing is left to offer, and the button goes
            const last = givable.length === 1;
            setBusy(true);
            void give(value).then((ok) => {
              setBusy(false);
              if (!ok) return;
              setPick("");
              close();
              if (last) focusAfterLast?.();
            });
          }}
        >
          <label className="field">
            {t("templates:templateGivePick")}
            <select value={value} onChange={(e) => setPick(e.target.value)}>
              {givable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <p className="muted small flush">{note}</p>
          <div className="row">
            <span className="grow" />
            <button type="submit" className="primary" disabled={busy}>
              {t("templates:templateGive")}
            </button>
          </div>
        </form>
      )}
    </Popover>
  );
}
